import { AfterViewInit, Component, computed, DestroyRef, effect, ElementRef, HostListener, inject, input, output, Renderer2, signal, viewChild } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { PreviewCustomRule } from '../../models/filter-models';
import { DisplayType } from "../../models/enums";
import { LabelViewerComponent } from '../label-viewer/label-viewer.component';
import { ItemLabelComponent } from '../item-label/item-label.component';
import { DivCardViewerComponent } from '../divcard-viewer/divcard-viewer.component';
import { ItemDataService } from '../../services/item-data.service';
import { SettingsService } from '../../services/settings.service';
import { CosmeticsGeneratorService } from '../../services/cosmetics-generator.service';
import { labelPreviewBgImageStyle, resolveLabelPreviewBgId } from '../label-preview-backgrounds';
import {
  groupRuleGroupsIntoStackedLayout,
  groupRulesIntoStackedLayout,
  LabelLayoutStyle,
  StackedLayout,
} from './label-layout.helper';
import { poe1 } from '../../../game';

const RULE_PREVIEW_HEIGHT_STORAGE_KEY = `${poe1 ? 'poe1' : 'poe'}-rule-preview-height`;
const RULE_PREVIEW_LAYOUT_STORAGE_KEY = `${poe1 ? 'poe1' : 'poe'}-rule-preview-layout`;

@Component({
  selector: 'app-rule-preview',
  imports: [LabelViewerComponent, ItemLabelComponent, DivCardViewerComponent, NgTemplateOutlet],
  templateUrl: './rule-preview.component.html',
  styleUrl: './rule-preview.component.scss',
  host: {
    '[class.rule-preview-resizable]': 'resizable()',
    '[class.rule-preview-compact]': 'compact()',
  },
})
export class RulePreviewComponent implements AfterViewInit {
  private readonly settings = inject(SettingsService);
  readonly showDropInfo = this.settings.showDropInfo;
  private readonly renderer = inject(Renderer2);
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject(ElementRef<HTMLElement>);

  private static readonly DEFAULT_HEIGHT_PX = 280;
  private static readonly MIN_HEIGHT_PX = 120;
  private static readonly MAX_HEIGHT_PX = 720;
  private static readonly HEIGHT_STORAGE_KEY = RULE_PREVIEW_HEIGHT_STORAGE_KEY;

  rules = input.required<PreviewCustomRule[]>();
  ruleGroups = input<PreviewCustomRule[][] | undefined>(undefined);
  forceGlow = input<boolean | null>(null);
  forceVisible = input(false);
  disableBeams = input(false);
  labelPreviewBgOverride = input<string | null>(null);
  layoutStyle = input<LabelLayoutStyle>('scattered');
  scrollable = input(false);
  largeBackground = input<boolean | null>(null);
  resizable = input(false);
  compact = input(false);

  itemLibraryClick = output<{ event: MouseEvent; rule: PreviewCustomRule }>();

  private static readonly BEAM_HIGHLIGHT_CLEAR_DEBOUNCE_MS = 200;
  private beamHighlightClearTimer?: ReturnType<typeof setTimeout>;

  private itemLibraryAnchorRect?: DOMRect;
  private previewResizeObserver?: ResizeObserver;
  private readonly previewContainerHeightPx = signal(RulePreviewComponent.DEFAULT_HEIGHT_PX);
  private readonly resizableHeightPx = signal(RulePreviewComponent.loadSavedHeight());
  private resizeStartY = 0;
  private resizeStartHeight = RulePreviewComponent.DEFAULT_HEIGHT_PX;
  private stopResize?: () => void;

  private readonly previewContainer = viewChild<ElementRef<HTMLElement>>('previewContainer');
  private readonly floatingTooltip = viewChild<ElementRef<HTMLElement>>('floatingTooltip');

  readonly hoveredItemLibraryHash = signal<string | undefined>(undefined);
  readonly hoveredLibraryRule = signal<PreviewCustomRule | undefined>(undefined);
  readonly itemLibraryTooltipPosition = signal<{ top: number; left: number } | undefined>(undefined);
  private tooltipHover = false;

  readonly highlightRule = signal<PreviewCustomRule | undefined>(undefined);

  labelBackgroundImage = computed(() =>
    labelPreviewBgImageStyle(
      resolveLabelPreviewBgId(this.labelPreviewBgOverride(), this.settings.labelPreviewBg()),
      this.largeBackground() ?? this.scrollable(),
    )
  );

  private readonly stackedLayoutOptions = computed(() => ({
    containerHeightPx: this.previewContainerHeightPx(),
    forceVisible: this.forceVisible(),
    isRuleHidden: (rule: PreviewCustomRule) =>
      this.cosmeticsGeneratorService.getCosmeticOptionsFromDisplayOptions(
        rule.displayOptions,
        this.resolveExampleName(rule),
      ).hidden,
  }));

  readonly stackedLayout = computed((): StackedLayout => {
    const options = this.stackedLayoutOptions();
    const groups = this.ruleGroups();
    if (groups?.length) {
      return groupRuleGroupsIntoStackedLayout(groups, options);
    }
    return groupRulesIntoStackedLayout(this.rules(), options);
  });

  readonly scatteredGroups = computed((): PreviewCustomRule[][] => {
    const groups = this.ruleGroups();
    if (groups?.length) {
      return groups.filter((group) => group.length > 0);
    }
    const rules = this.rules();
    return rules.length > 0 ? [rules] : [];
  });

  readonly layoutRules = computed((): PreviewCustomRule[] => {
    const groups = this.ruleGroups();
    if (groups?.length) {
      return groups.flat();
    }
    return this.rules();
  });

  readonly exampleNames = computed(() => {
    const names = new Map<number, string>();
    for (const rule of this.layoutRules()) {
      names.set(rule.order, this.resolveExampleName(rule));
    }
    return names;
  });

  readonly stackSizes = computed(() => {
    const sizes = new Map<number, number>();
    for (const rule of this.layoutRules()) {
      const size = this.resolveStackSize(rule);
      if (size) sizes.set(rule.order, size);
    }
    return sizes;
  });

  readonly dimmedRuleOrders = computed(() => {
    const dimmed = new Set<number>();
    const disableBeams = this.disableBeams();
    const highlightRule = this.highlightRule();
    const hoveredHash = this.hoveredItemLibraryHash();

    for (const rule of this.layoutRules()) {
      if (disableBeams) {
        dimmed.add(rule.order);
      } else if (highlightRule && highlightRule !== rule) {
        dimmed.add(rule.order);
      } else if (
        hoveredHash &&
        (rule.isItemLibraryItem || rule.isRandomLootItem || rule.isSimulatedTierItem) &&
        rule.itemLibraryHash !== hoveredHash
      ) {
        dimmed.add(rule.order);
      }
    }
    return dimmed;
  });

  readonly glowDimmedRuleOrders = computed(() => {
    const dimmed = new Set<number>();
    const highlightRule = this.highlightRule();
    const hoveredHash = this.hoveredItemLibraryHash();

    for (const rule of this.layoutRules()) {
      if (highlightRule && highlightRule !== rule) {
        dimmed.add(rule.order);
      } else if (
        hoveredHash &&
        (rule.isItemLibraryItem || rule.isRandomLootItem || rule.isSimulatedTierItem) &&
        rule.itemLibraryHash !== hoveredHash
      ) {
        dimmed.add(rule.order);
      }
    }
    return dimmed;
  });

  currencyItemTypes!: string[];

  DisplayType = DisplayType;

  constructor(
    private itemDataService: ItemDataService,
    private cosmeticsGeneratorService: CosmeticsGeneratorService,
  ) {
    this.currencyItemTypes = this.itemDataService.getCurrencyItemTypes();

    effect(() => {
      if (this.previewContainer()) {
        this.observePreviewContainerSize();
      }
    });

    effect(() => {
      const ref = this.floatingTooltip();
      if (!ref?.nativeElement) return;
      this.moveTooltipToBody(ref.nativeElement);
      requestAnimationFrame(() => this.adjustFloatingTooltipPosition());
    });

    this.destroyRef.onDestroy(() => {
      const tooltip = this.floatingTooltip()?.nativeElement;
      if (tooltip?.parentElement === document.body) {
        this.renderer.removeChild(document.body, tooltip);
      }
      this.previewResizeObserver?.disconnect();
      this.cancelBeamHighlightClear();
      this.stopResize?.();
    });
  }

  ngAfterViewInit(): void {
    if (this.resizable()) {
      this.applyResizableHeight(this.resizableHeightPx());
    }
    this.observePreviewContainerSize();
  }

  onResizeHandleMouseDown(event: MouseEvent): void {
    if (!this.resizable()) return;

    event.preventDefault();
    event.stopPropagation();

    this.resizeStartY = event.clientY;
    this.resizeStartHeight = this.resizableHeightPx();

    const onMove = (moveEvent: MouseEvent) => {
      const nextHeight = RulePreviewComponent.clampHeight(
        this.resizeStartHeight + (moveEvent.clientY - this.resizeStartY),
      );
      this.applyResizableHeight(nextHeight);
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
      this.stopResize = undefined;
      localStorage.setItem(
        RulePreviewComponent.HEIGHT_STORAGE_KEY,
        String(this.resizableHeightPx()),
      );
    };

    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    this.stopResize = onUp;
  }

  private applyResizableHeight(height: number): void {
    if (!this.resizable()) return;

    this.resizableHeightPx.set(height);
    this.host.nativeElement.style.setProperty('--rule-preview-height', `${height}px`);
    this.previewContainerHeightPx.set(height);
  }

  private static loadSavedHeight(): number {
    const raw = localStorage.getItem(RulePreviewComponent.HEIGHT_STORAGE_KEY);
    const parsed = raw ? Number.parseInt(raw, 10) : Number.NaN;
    return Number.isFinite(parsed)
      ? RulePreviewComponent.clampHeight(parsed)
      : RulePreviewComponent.DEFAULT_HEIGHT_PX;
  }

  private static clampHeight(height: number): number {
    return Math.min(
      RulePreviewComponent.MAX_HEIGHT_PX,
      Math.max(RulePreviewComponent.MIN_HEIGHT_PX, Math.round(height)),
    );
  }

  static loadSavedLayoutStyle(): LabelLayoutStyle {
    const raw = localStorage.getItem(RULE_PREVIEW_LAYOUT_STORAGE_KEY);
    return raw === 'stacked' || raw === 'scattered' ? raw : 'stacked';
  }

  static saveLayoutStyle(style: LabelLayoutStyle): void {
    localStorage.setItem(RULE_PREVIEW_LAYOUT_STORAGE_KEY, style);
  }

  private observePreviewContainerSize(): void {
    const element = this.previewContainer()?.nativeElement;
    if (!element) return;

    this.previewResizeObserver?.disconnect();
    this.previewResizeObserver = new ResizeObserver((entries) => {
      const height = Math.round(entries[0]?.contentRect.height ?? 0);
      if (height > 0 && height !== this.previewContainerHeightPx()) {
        this.previewContainerHeightPx.set(height);
      }
    });
    this.previewResizeObserver.observe(element);
    const initialHeight = Math.round(element.getBoundingClientRect().height);
    if (initialHeight > 0) {
      this.previewContainerHeightPx.set(initialHeight);
    }
  }

  onMouseEnter(rule: PreviewCustomRule): void {
    this.cancelBeamHighlightClear();
    if (!rule.isItemLibraryItem && !rule.isSimulatedTierItem) {
      this.hoveredItemLibraryHash.set(undefined);
      this.clearDetailPanel();
    }
    this.highlightRule.set(rule);
  }

  onMouseLeave(rule: PreviewCustomRule): void {
    if (!rule.isItemLibraryItem && !rule.isSimulatedTierItem) {
      this.scheduleBeamHighlightClear();
    } else if (this.highlightRule() === rule) {
      this.clearDetailPanel();
      this.scheduleBeamHighlightClear();
    }
  }

  onItemLibraryMouseEnter(rule: PreviewCustomRule, event: MouseEvent): void {
    this.cancelBeamHighlightClear();
    const anchor = event.currentTarget as HTMLElement;
    this.itemLibraryAnchorRect = anchor.getBoundingClientRect();
    this.itemLibraryTooltipPosition.set({
      top: this.itemLibraryAnchorRect.bottom + 6,
      left: this.itemLibraryAnchorRect.left + this.itemLibraryAnchorRect.width / 2,
    });
    if (rule.itemLibraryHash) {
      this.hoveredItemLibraryHash.set(rule.itemLibraryHash);
    }
    this.hoveredLibraryRule.set(rule);
    this.highlightRule.set(rule);
    requestAnimationFrame(() => this.adjustFloatingTooltipPosition());
  }

  onItemLibraryMouseLeave(event: MouseEvent): void {
    if (this.settings.keepItemLabelTooltipsOpenOnHover()) {
      const related = event.relatedTarget as Node | null;
      if (related && this.floatingTooltip()?.nativeElement.contains(related)) {
        return;
      }
    }
    this.clearDetailPanel();
    this.scheduleBeamHighlightClear();
  }

  onTooltipMouseEnter(): void {
    if (!this.settings.keepItemLabelTooltipsOpenOnHover()) return;
    this.tooltipHover = true;
  }

  onItemLibraryTooltipMouseLeave(): void {
    if (!this.settings.keepItemLabelTooltipsOpenOnHover()) return;
    this.tooltipHover = false;
    this.clearDetailPanel();
    this.scheduleBeamHighlightClear();
  }

  private scheduleBeamHighlightClear(): void {
    this.cancelBeamHighlightClear();
    this.beamHighlightClearTimer = setTimeout(() => {
      this.beamHighlightClearTimer = undefined;
      this.clearBeamHighlight();
    }, RulePreviewComponent.BEAM_HIGHLIGHT_CLEAR_DEBOUNCE_MS);
  }

  private cancelBeamHighlightClear(): void {
    if (this.beamHighlightClearTimer !== undefined) {
      clearTimeout(this.beamHighlightClearTimer);
      this.beamHighlightClearTimer = undefined;
    }
  }

  private clearDetailPanel(): void {
    this.hoveredLibraryRule.set(undefined);
    this.itemLibraryTooltipPosition.set(undefined);
    this.itemLibraryAnchorRect = undefined;
  }

  private clearBeamHighlight(): void {
    this.hoveredItemLibraryHash.set(undefined);
    this.highlightRule.set(undefined);
  }

  private moveTooltipToBody(tooltip: HTMLElement): void {
    if (tooltip.parentElement !== document.body) {
      this.renderer.appendChild(document.body, tooltip);
    }
  }

  private adjustFloatingTooltipPosition(): void {
    const tooltip = this.floatingTooltip()?.nativeElement;
    const anchorRect = this.itemLibraryAnchorRect;
    if (!tooltip || !anchorRect) return;

    const margin = 8;
    const gap = 6;
    const anchorCenter = anchorRect.left + anchorRect.width / 2;
    tooltip.style.top = `${anchorRect.bottom + gap}px`;
    tooltip.style.left = `${anchorCenter}px`;
    tooltip.style.transform = 'translateX(-50%)';

    const rect = tooltip.getBoundingClientRect();
    let left = anchorCenter;

    if (rect.left < margin) {
      left += margin - rect.left;
    } else if (rect.right > window.innerWidth - margin) {
      left -= rect.right - (window.innerWidth - margin);
    }

    tooltip.style.left = `${left}px`;
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'c' || (!event.ctrlKey && !event.metaKey)) return;
    if (this.isTextInputFocused()) return;

    const text = this.hoveredLibraryRule()?.itemLibraryText;
    if (!text?.startsWith('Item Class: ')) return;

    event.preventDefault();
    void navigator.clipboard.writeText(text);
  }

  private isTextInputFocused(): boolean {
    const activeElement = document.activeElement;
    return !!(activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.hasAttribute('contenteditable')
    ));
  }

  onLabelClick(event: MouseEvent, rule: PreviewCustomRule): void {
    if (!rule.librarySimulatorResult) return;
    if (rule.isItemLibraryItem && rule.itemLibraryHash) {
      this.itemLibraryClick.emit({ event, rule });
    } else if (rule.isRandomLootItem && rule.itemLibraryHash) {
      this.itemLibraryClick.emit({ event, rule });
    } else if (rule.isSimulatedTierItem) {
      this.itemLibraryClick.emit({ event, rule });
    }
  }

  private resolveExampleName(rule: PreviewCustomRule): string {
    if (!rule) return 'Example';
    if (rule.exampleName) return rule.exampleName;

    if (rule.baseTypes?.length && !rule.baseTypes.includes('All')) return rule.baseTypes[0];

    if (rule.itemType === 'All') return 'Example';
    if (rule.itemType === 'All Gear') return 'Example Gear';
    if (rule.itemType === 'All Weapons') return 'Example Weapon';
    if (rule.itemType === 'All Armour') return 'Example Armour';
    if (rule.itemType === 'All Augments') return 'Example Augment';
    if (rule.itemType === 'All Runes') return 'Example Rune';
    if (rule.itemType === 'All Gems') return 'Example Gem';
    if (rule.itemType === 'All Uncut Gems') return 'Example Uncut Gem';

    return 'All ' + rule.itemType;
  }

  private resolveStackSize(rule: PreviewCustomRule): number | undefined {
    return rule.stackSize
      ?? rule.advancedConditions?.minStackSize
      ?? rule.librarySimulatorResult?.item.stackSize;
  }
}
